import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./assest_design/CommercialAutomation.css";

import mainAutomationImg from "./assets/commercial-automation/main-image.png";
import staffTrackingImg from "./assets/commercial-automation/Staff-tracking.png";
import smartAccessImg from "./assets/commercial-automation/smart-access-control.png";
import assetTrackingImg from "./assets/commercial-automation/assest-tracking.png";
import coldRoomImg from "./assets/commercial-automation/cold-room.png";
import stockTrackingImg from "./assets/commercial-automation/stock-tracking.png";
import vehicleTrackingImg from "./assets/commercial-automation/vehicle-tracking.png";

function CommercialAutomation() {
  const navigate = useNavigate();

  useEffect(() => {
    const pageTitle =
      "Commercial IoT & Automation Solutions South Africa | Smart Monitoring, Tracking & Dashboards | ProCode ZA";

    const pageDescription =
      "ProCode ZA builds commercial IoT and automation solutions in South Africa, including smart sensor monitoring, access control, asset tracking, cold room temperature monitoring, inventory automation, fleet tracking, offline-ready dashboards and connected business systems for African operating conditions.";

    const pageKeywords =
      "commercial IoT solutions South Africa, IoT automation South Africa, business automation South Africa, smart monitoring systems, environmental monitoring systems, site monitoring sensors, access control systems South Africa, asset tracking systems South Africa, cold room temperature monitoring, medical storage monitoring, inventory automation South Africa, warehouse stock tracking, fleet tracking South Africa, delivery monitoring software, offline business systems, IoT dashboards, smart sensor systems, ProCode ZA IoT, ProCode ZA automation, African business technology, commercial automation for warehouses, automation for farms, automation for clinics, automation for logistics companies, smart operations systems";

    const pageUrl = "https://procodeza.co.za/commercial-automation";
    const pageImage =
      "https://procodeza.co.za/assets/commercial-automation/main-image.png";

    document.title = pageTitle;

    const setMeta = (name, content, property = false) => {
      const selector = property
        ? `meta[property="${name}"]`
        : `meta[name="${name}"]`;

      let tag = document.querySelector(selector);

      if (!tag) {
        tag = document.createElement("meta");

        if (property) {
          tag.setAttribute("property", name);
        } else {
          tag.setAttribute("name", name);
        }

        document.head.appendChild(tag);
      }

      tag.setAttribute("content", content);
    };

    const setLink = (rel, href) => {
      let link = document.querySelector(`link[rel="${rel}"]`);

      if (!link) {
        link = document.createElement("link");
        link.setAttribute("rel", rel);
        document.head.appendChild(link);
      }

      link.setAttribute("href", href);
    };

    const setJsonLd = (id, data) => {
      let script = document.getElementById(id);

      if (!script) {
        script = document.createElement("script");
        script.id = id;
        script.type = "application/ld+json";
        document.head.appendChild(script);
      }

      script.textContent = JSON.stringify(data);
    };

    setMeta("description", pageDescription);
    setMeta("keywords", pageKeywords);
    setMeta("robots", "index, follow, max-image-preview:large");
    setMeta("author", "ProCode ZA");
    setMeta("theme-color", "#00909f");

    setMeta("og:title", pageTitle, true);
    setMeta("og:description", pageDescription, true);
    setMeta("og:type", "website", true);
    setMeta("og:url", pageUrl, true);
    setMeta("og:image", pageImage, true);
    setMeta("og:image:alt", "Commercial IoT and automation dashboard for South African businesses", true);
    setMeta("og:site_name", "ProCode ZA", true);
    setMeta("og:locale", "en_ZA", true);

    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", pageTitle);
    setMeta("twitter:description", pageDescription);
    setMeta("twitter:image", pageImage);
    setMeta("twitter:image:alt", "Commercial IoT and automation systems in South Africa");

    setLink("canonical", pageUrl);

    setJsonLd("commercial-automation-service-schema", {
      "@context": "https://schema.org",
      "@type": "Service",
      name: "Commercial IoT & Automation Solutions",
      description: pageDescription,
      provider: {
        "@type": "Organization",
        name: "ProCode ZA",
        url: "https://procodeza.co.za",
        areaServed: {
          "@type": "Country",
          name: "South Africa",
        },
      },
      serviceType: [
        "Commercial IoT Solutions",
        "Business Automation",
        "Environmental Monitoring",
        "Access Control",
        "Asset Tracking",
        "Cold Room Monitoring",
        "Inventory Automation",
        "Fleet Monitoring",
        "Offline-Ready Dashboard Development",
      ],
      areaServed: [
        {
          "@type": "Country",
          name: "South Africa",
        },
        {
          "@type": "Place",
          name: "Africa",
        },
      ],
      audience: {
        "@type": "Audience",
        audienceType:
          "Warehouses, farms, clinics, schools, logistics companies, factories, retailers, security companies and field service teams",
      },
      url: pageUrl,
    });

    setJsonLd("commercial-automation-faq-schema", {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: [
        {
          "@type": "Question",
          name: "What commercial IoT solutions does ProCode ZA build?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "ProCode ZA builds commercial IoT and automation systems such as smart sensor monitoring, access control, asset tracking, cold room monitoring, inventory automation, fleet tracking and offline-ready dashboards for South African businesses.",
          },
        },
        {
          "@type": "Question",
          name: "Can ProCode ZA build offline-ready automation systems?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Yes. ProCode ZA designs automation systems that can keep capturing key records during weak connectivity and sync data when the connection returns.",
          },
        },
        {
          "@type": "Question",
          name: "Which industries can use commercial automation?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Commercial automation can support warehouses, retail stores, farms, clinics, schools, logistics companies, construction sites, restaurants, security companies, offices and field service teams.",
          },
        },
      ],
    });
  }, []);

  const automationSolutions = [
    {
      title: "Environmental & Site Monitoring",
      tag: "Sensors · Conditions · Alerts",
      image: staffTrackingImg,
      imageAlt:
        "IoT sensors monitoring a commercial site environment in South Africa",
      description:
        "Monitor real-world site conditions such as temperature, humidity, air quality, water levels, power status, equipment activity and safety-related environmental changes using connected sensors and live dashboards.",
      features: [
        "Temperature, humidity and air-quality sensors",
        "Water level and power status monitoring",
        "Live alerts when conditions change",
        "Dashboard views for site and operations teams",
      ],
    },
    {
      title: "Smart Access Control",
      tag: "Security · Entry · Permissions",
      image: smartAccessImg,
      imageAlt:
        "South African business professionals entering a secure workplace using smart access control",
      description:
        "Control who enters restricted areas using smart locks, access cards, biometric verification or coded entry linked to permission levels.",
      features: [
        "Role-based access permissions",
        "Access logs for audit trails",
        "Gate, office, storage or server room control",
        "Alerts for unusual access activity",
      ],
    },
    {
      title: "Asset & Equipment Tracking",
      tag: "Assets · Tools · Accountability",
      image: assetTrackingImg,
      imageAlt:
        "Locomotive asset tracking system shown on a tablet in a train yard",
      description:
        "Monitor high-value equipment, tools, vehicles, laptops or commercial assets using QR codes, RFID tags, GPS modules or check-in/check-out workflows.",
      features: [
        "Track who has which asset",
        "Maintenance and inspection reminders",
        "Lost or overdue asset alerts",
        "Asset history and condition records",
      ],
    },
    {
      title: "Cold Room & Temperature Monitoring",
      tag: "Food · Medical · Storage",
      image: coldRoomImg,
      imageAlt:
        "Medical cold storage room with temperature sensors and digital monitoring",
      description:
        "Use IoT sensors to monitor temperature-sensitive environments such as cold rooms, fridges, stock rooms, medical storage or food production areas.",
      features: [
        "Live temperature and humidity readings",
        "SMS, email or dashboard alerts",
        "Offline logging during connectivity issues",
        "Compliance-ready temperature history",
      ],
    },
    {
      title: "Inventory & Stock Movement Automation",
      tag: "Stock · Warehouse · Retail",
      image: stockTrackingImg,
      imageAlt:
        "Warehouse worker checking inventory levels on a tablet beside organised shelves",
      description:
        "Help teams track stock movement, low inventory, receiving, dispatching and item counts using barcode scanning, QR codes and connected dashboards.",
      features: [
        "Barcode or QR stock scanning",
        "Low-stock alerts",
        "Stock movement history",
        "Warehouse and branch-level views",
      ],
    },
    {
      title: "Vehicle, Fleet & Delivery Monitoring",
      tag: "Fleet · Logistics · Field Teams",
      image: vehicleTrackingImg,
      imageAlt:
        "South African logistics team monitoring delivery vans and fleet routes using a tablet",
      description:
        "Support field operations by tracking vehicles, deliveries, driver activity, route completion, fuel-related events and service schedules.",
      features: [
        "GPS or route tracking integrations",
        "Driver check-in workflows",
        "Delivery completion records",
        "Maintenance and service reminders",
      ],
    },
  ];

  const offlineCapabilities = [
    {
      title: "Offline-first data capture",
      text: "Field teams can still capture attendance, stock, inspections or service records even when connectivity is weak.",
    },
    {
      title: "Automatic sync when online",
      text: "Once the device reconnects, records can sync back to the main dashboard without losing important operational data.",
    },
    {
      title: "Local backup options",
      text: "For critical environments, systems can store temporary local records on the device, gateway or internal network.",
    },
    {
      title: "Built for unstable environments",
      text: "The goal is not to assume perfect Wi-Fi. We design around real South African and African operating conditions.",
    },
  ];

  const trackingIdeas = [
    "Temperature monitoring",
    "Humidity tracking",
    "Air quality readings",
    "Water tank levels",
    "Electricity usage",
    "Generator activity",
    "Site access logs",
    "Stock movement",
    "Equipment usage",
    "Tool check-in and check-out",
    "Cold room temperature",
    "Vehicle routes",
    "Maintenance schedules",
    "Safety inspections",
    "Delivery completion",
    "Cleaning logs",
    "Production counts",
    "Machine downtime",
  ];

  const industries = [
    "Warehouses",
    "Retail stores",
    "Manufacturing sites",
    "Farms and hydroponics",
    "Schools and campuses",
    "Clinics and medical storage",
    "Construction sites",
    "Restaurants and food suppliers",
    "Logistics companies",
    "Security companies",
    "Office buildings",
    "Field service teams",
  ];

  return (
    <div className="commercial-automation-wrapper">
      <main>
        <section className="ca-hero-section ca-hero-animated" id="top">
          <div className="ca-hero-content">
            <div className="ca-hero-badge">
              Commercial IoT · Automation · Offline Systems
            </div>

            <h1>
              Commercial IoT and automation solutions for South African
              businesses that need better control, tracking and visibility.
            </h1>

            <p>
              ProCode ZA designs practical commercial IoT and automation systems
              for businesses that need to monitor sites, assets, stock,
              vehicles, machines, environmental conditions and daily operational
              activity. From smart sensors and access control to cold room
              monitoring, fleet visibility and offline-ready dashboards, we help
              teams replace manual processes with connected business systems
              built for African conditions.
            </p>

            <div className="ca-hero-actions">
              <button
                className="ca-cta-primary"
                type="button"
                onClick={() => navigate("/start-project")}
              >
                Start an Automation Project
              </button>

              <button
                className="ca-cta-outline ca-dark-outline"
                type="button"
                onClick={() => navigate("/contact")}
              >
                Speak to ProCode ZA
              </button>
            </div>

            <div className="ca-trust-wrapper">
              <div className="ca-trust-pill ca-pill-light">
                Smart sensor monitoring
              </div>
              <div className="ca-trust-pill ca-pill-dark">
                Offline-ready tracking
              </div>
              <div className="ca-trust-pill ca-pill-light">
                IoT dashboards and alerts
              </div>
            </div>
          </div>

          <div className="ca-hero-visual ca-float-gentle">
            <div className="ca-device-card">
              <div className="ca-device-topbar">
                <span></span>
                <span></span>
                <span></span>
              </div>

              <div className="ca-dashboard-preview">
                <div className="ca-preview-header">
                  <div>
                    <small>Live Operations</small>
                    <strong>Commercial Control Hub</strong>
                  </div>
                  <span className="ca-live-dot">Live</span>
                </div>

                <div className="ca-preview-grid">
                  <div>
                    <strong>128</strong>
                    <span>Records captured</span>
                  </div>
                  <div>
                    <strong>42</strong>
                    <span>Assets tracked</span>
                  </div>
                  <div>
                    <strong>8</strong>
                    <span>Alerts today</span>
                  </div>
                  <div>
                    <strong>99%</strong>
                    <span>Sync health</span>
                  </div>
                </div>

                <div className="ca-sensor-lines">
                  <div>
                    <span>Cold room sensor</span>
                    <strong>Stable</strong>
                  </div>
                  <div>
                    <span>Warehouse scanner</span>
                    <strong>Online</strong>
                  </div>
                  <div>
                    <span>Site monitoring gateway</span>
                    <strong>Synced</strong>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="ca-intro-section ca-reveal-up" id="what-it-is">
          <div className="ca-section-heading">
            <span className="ca-small-label">What this means</span>
            <h2>
              Commercial automation connects your real-world operations to
              useful digital systems.
            </h2>
            <p>
              Instead of relying only on paper registers, WhatsApp updates,
              spreadsheets and manual checks, your business can use connected
              devices, sensors, scanners and dashboards to capture operational
              information automatically and turn it into clear, useful decisions.
            </p>
          </div>

          <div className="ca-explain-grid">
            <div className="ca-explain-card ca-hover-lift">
              <h3>Capture</h3>
              <p>
                Devices collect real-world information such as temperature,
                humidity, movement, access events, stock scans, equipment usage,
                vehicle activity or machine performance.
              </p>
            </div>

            <div className="ca-explain-card ca-hover-lift">
              <h3>Store</h3>
              <p>
                Data is saved securely in a database, local device, cloud
                platform or offline-first system depending on the client’s
                environment and connectivity.
              </p>
            </div>

            <div className="ca-explain-card ca-hover-lift">
              <h3>Monitor</h3>
              <p>
                Managers view dashboards, alerts, reports and trends so they can
                understand what is happening across sites, branches, teams and
                assets.
              </p>
            </div>

            <div className="ca-explain-card ca-hover-lift">
              <h3>Act</h3>
              <p>
                The system can trigger notifications, reminders, escalations,
                exports, access rules, service tasks or automated workflows.
              </p>
            </div>
          </div>
        </section>

        <section className="ca-image-strip-section ca-reveal-up">
          <div className="ca-main-image-card">
            <img
              src={mainAutomationImg}
              alt="Commercial IoT automation dashboard with warehouse tracking, environmental monitoring, access control and operations visibility"
              className="ca-main-image"
            />
          </div>
        </section>

        <section className="ca-solutions-section ca-reveal-up" id="solutions">
          <div className="ca-section-heading">
            <span className="ca-small-label">Commercial IoT services</span>
            <h2>
              Practical automation systems ProCode ZA can design for South
              African businesses.
            </h2>
            <p>
              These solutions help businesses improve visibility,
              accountability, safety, reporting and day-to-day operational
              control across physical environments.
            </p>
          </div>

          <div className="ca-solutions-grid">
            {automationSolutions.map((solution) => (
              <article
                className="ca-solution-card ca-hover-lift"
                key={solution.title}
              >
                <div className="ca-card-image-wrap">
                  <img
                    src={solution.image}
                    alt={solution.imageAlt}
                    className="ca-solution-image"
                  />
                </div>

                <div className="ca-solution-content">
                  <span className="ca-solution-tag">{solution.tag}</span>
                  <h3>{solution.title}</h3>
                  <p>{solution.description}</p>

                  <ul>
                    {solution.features.map((feature) => (
                      <li key={feature}>{feature}</li>
                    ))}
                  </ul>
                </div>
              </article>
            ))}
          </div>
        </section>

        <section className="ca-offline-section ca-reveal-up" id="offline">
          <div className="ca-offline-container">
            <div className="ca-offline-text">
              <span className="ca-small-label ca-light-label">
                Built for real environments
              </span>

              <h2>
                Offline-ready IoT and automation systems for teams that cannot
                depend on perfect internet.
              </h2>

              <p>
                Many commercial environments do not have stable connectivity all
                day. Warehouses, farms, security posts, factories, construction
                sites, school campuses, clinics and delivery teams may still
                need to capture important records even when Wi-Fi or mobile data
                drops.
              </p>

              <p>
                ProCode ZA can design automation systems that continue working
                locally and then sync when the connection returns. This makes
                the system more practical for South African and African business
                conditions.
              </p>
            </div>

            <div className="ca-offline-grid">
              {offlineCapabilities.map((item) => (
                <div className="ca-offline-card" key={item.title}>
                  <h3>{item.title}</h3>
                  <p>{item.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="ca-tracking-section ca-reveal-up" id="tracking">
          <div className="ca-section-heading">
            <span className="ca-small-label">
              Things your business can monitor
            </span>
            <h2>
              From sensors and stock to machines, vehicles, cold rooms and
              environmental readings.
            </h2>
            <p>
              The exact system depends on your business, but these are the kinds
              of operational signals that can be captured and turned into useful
              dashboards, alerts and reports.
            </p>
          </div>

          <div className="ca-chip-grid">
            {trackingIdeas.map((item) => (
              <span className="ca-chip" key={item}>
                {item}
              </span>
            ))}
          </div>
        </section>

        <section className="ca-dashboard-section ca-reveal-up">
          <div className="ca-dashboard-left">
            <span className="ca-small-label">Dashboards and reporting</span>
            <h2>Give managers one place to see what is happening.</h2>

            <p>
              The goal is not only to collect data. The goal is to make that
              data useful. ProCode ZA can build dashboards where managers can
              view live activity, compare branches, download reports, receive
              alerts and spot operational issues earlier.
            </p>

            <div className="ca-dashboard-bullets">
              <div>
                <strong>Live alerts</strong>
                <span>Notify managers when something needs attention.</span>
              </div>

              <div>
                <strong>Report exports</strong>
                <span>Download CSV, Excel or PDF reports for records.</span>
              </div>

              <div>
                <strong>Role-based views</strong>
                <span>
                  Give owners, managers and operational teams different access
                  levels.
                </span>
              </div>
            </div>
          </div>

          <div className="ca-dashboard-right">
            <div className="ca-report-card ca-card-glow">
              <div className="ca-report-header">
                <div>
                  <small>Today</small>
                  <h3>Operations Snapshot</h3>
                </div>
                <span>Auto-synced</span>
              </div>

              <div className="ca-report-bars">
                <div>
                  <span>Sensor readings captured</span>
                  <div className="ca-bar">
                    <span style={{ width: "86%" }}></span>
                  </div>
                </div>

                <div>
                  <span>Stock scans completed</span>
                  <div className="ca-bar">
                    <span style={{ width: "72%" }}></span>
                  </div>
                </div>

                <div>
                  <span>Assets verified</span>
                  <div className="ca-bar">
                    <span style={{ width: "64%" }}></span>
                  </div>
                </div>

                <div>
                  <span>System uptime</span>
                  <div className="ca-bar">
                    <span style={{ width: "94%" }}></span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="ca-industries-section ca-reveal-up" id="industries">
          <div className="ca-section-heading">
            <span className="ca-small-label">Who this is for</span>
            <h2>
              Commercial IoT and automation for environments where visibility
              matters.
            </h2>
            <p>
              Commercial automation can support any business that needs better
              control over sites, stock, equipment, movement, safety,
              temperature, assets or environmental conditions.
            </p>
          </div>

          <div className="ca-industry-grid">
            {industries.map((industry) => (
              <div className="ca-industry-card ca-hover-lift" key={industry}>
                {industry}
              </div>
            ))}
          </div>
        </section>

        <section className="ca-process-section ca-reveal-up" id="process">
          <div className="ca-section-heading">
            <span className="ca-small-label">How we approach it</span>
            <h2>From idea to working automation system.</h2>
          </div>

          <div className="ca-process-timeline">
            <div className="ca-process-step">
              <span>01</span>
              <h3>Understand the operation</h3>
              <p>
                We map what needs to be monitored, who uses the system, where
                data is captured and what problems the business currently faces.
              </p>
            </div>

            <div className="ca-process-step">
              <span>02</span>
              <h3>Design the automation flow</h3>
              <p>
                We define the devices, sensors, user roles, offline needs,
                dashboards, alerts and reporting structure.
              </p>
            </div>

            <div className="ca-process-step">
              <span>03</span>
              <h3>Build and test the system</h3>
              <p>
                We develop the software, connect the hardware logic and test the
                system against realistic business conditions.
              </p>
            </div>

            <div className="ca-process-step">
              <span>04</span>
              <h3>Deploy, train and support</h3>
              <p>
                We help teams understand the system, use it confidently and
                improve it over time as the business grows.
              </p>
            </div>
          </div>
        </section>

        <section className="ca-final-cta ca-reveal-up" id="contact">
          <div className="ca-final-card">
            <span className="ca-small-label ca-light-label">
              Build smarter operations
            </span>

            <h2>
              Let’s turn your manual commercial process into a smarter connected
              system.
            </h2>

            <p>
              Whether you need sensor monitoring, access control, stock
              tracking, cold room monitoring, asset management, fleet visibility
              or a custom IoT dashboard, ProCode ZA can help you shape the right
              solution.
            </p>

            <div className="ca-hero-actions ca-center-actions">
              <button
                className="ca-cta-primary ca-light-primary"
                type="button"
                onClick={() => navigate("/start-project")}
              >
                Start a Project
              </button>

              <button
                className="ca-cta-outline ca-light-outline"
                type="button"
                onClick={() => navigate("/contact")}
              >
                Contact Us
              </button>
            </div>
          </div>
        </section>
      </main>

      <footer className="ca-footer">
        <p>
          © {new Date().getFullYear()} ProCode ZA · Commercial IoT,
          automation systems, dashboards and offline-ready business technology
          for South Africa and Africa.
        </p>
      </footer>
    </div>
  );
}

export default CommercialAutomation;